import type { ReactNode } from "react";

import {
  SITE_URL,
  baseCrumbs,
  orgNode,
  faqJsonld,
  breadcrumbJsonld,
  webPageNode,
  type LearnFAQ,
  type LearnPageData,
} from "./learnShared";

const P = (s: ReactNode): ReactNode => <p>{s}</p>;

const faqs: LearnFAQ[] = [
  {
    question: "What options are left when nothing works for my depression?",
    answer:
      "If you have tried multiple antidepressants and therapy without lasting relief, you may have treatment-resistant depression. Options that many people have not yet tried include FDA-cleared TMS, SAINT® accelerated TMS (5 days, fMRI-guided), SPRAVATO® (esketamine), and ECT for severe cases. A psychiatric evaluation can identify which option may be appropriate for you.",
  },
  {
    question: "Is it normal to feel like nothing works for depression?",
    answer:
      "It is common. Roughly one-third of people treated for major depressive disorder do not reach remission after two treatment steps. This does not mean your depression cannot be treated — it means the treatments tried so far were not the right fit. Non-medication treatments like TMS and SAINT® work through different mechanisms than antidepressants.",
  },
  {
    question: "What if I've lost hope after trying several treatments?",
    answer:
      "Persistent depression after multiple treatments is exhausting and demoralizing, but it does not mean no options remain. Many patients who felt hopeless after years of treatment have found relief with TMS, SAINT®, or SPRAVATO®. If you are having thoughts of self-harm, please call or text 988 (Suicide & Crisis Lifeline) now — you do not have to be in crisis to call.",
  },
];

export const pageNothingWorks: LearnPageData = {
  slug: "what-options-are-left-when-nothing-works-for-depression",
  title: "What Options Are Left When Nothing Works? | Neuro Wellness",
  description:
    "If nothing has worked for your depression, options include TMS, SAINT®, SPRAVATO®, and ECT. You are not out of options. Learn what to try next.",
  canonical: "/learn/what-options-are-left-when-nothing-works-for-depression",
  h1: "What Options Are Left When Nothing Works for Depression?",
  breadcrumbLabel: "When Nothing Works for Depression",
  crumbs: baseCrumbs("When Nothing Works for Depression"),
  shortAnswer:
    "If you have tried multiple antidepressants and therapy without lasting relief, you may have treatment-resistant depression. Options that many people have not yet tried include FDA-cleared TMS, SAINT® accelerated TMS (5 days, fMRI-guided), SPRAVATO® (esketamine), and ECT for severe cases. A psychiatric evaluation can identify which option may be appropriate for you. If you are having thoughts of self-harm, call or text 988 now.",
  keyTakeaways: [
    "Roughly one-third of people do not reach remission after two treatment steps.",
    "TMS and SAINT® work through different mechanisms than antidepressants.",
    "SAINT® delivers treatment in 5 days instead of 6 weeks.",
    "SPRAVATO® is FDA-approved for treatment-resistant depression.",
    "If you are in crisis, call or text 988 now.",
  ],

  sections: [
    {
      heading: "You Are Not Out of Options",
      body: (
        <>
          {P(
            "Feeling like nothing works is one of the most common and most painful experiences in treatment-resistant depression. Roughly one-third of people treated for major depressive disorder do not reach remission after two treatment steps (STAR*D). This does not mean your depression cannot be treated — it means the treatments tried so far were not the right fit.",
          )}
        </>
      ),
    },
    {
      heading: "Treatments That Work Differently",
      body: (
        <>
          {P(
            "Antidepressants work through chemical neurotransmitter systems. TMS and SAINT® use magnetic pulses to stimulate specific brain regions involved in mood regulation — a completely different mechanism. SPRAVATO® (esketamine) works through the NMDA receptor system. If medications that target serotonin or norepinephrine have not worked, treatments that target brain activity directly may still help.",
          )}
        </>
      ),
    },
    {
      heading: "What to Do Next",
      body: (
        <>
          {P(
            "Ask a psychiatrist to review your full treatment history, confirm whether your trials were adequate, and discuss treatments you have not yet tried. A free consultation at Neuro Wellness TMS Centers of America can help you understand whether TMS, SAINT®, or SPRAVATO® may be appropriate.",
          )}
          {P(
            <>
              <a
                href="/depression-treatment-not-working"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                See our full guide on when treatment isn't working
              </a>
              .
            </>,
          )}
        </>
      ),
    },
  ],

  faqs,
  sources: [
    {
      citation: "Rush AJ, et al. STAR*D report. Am J Psychiatry. 2006.",
      url: "https://pubmed.ncbi.nlm.nih.gov/17074942/",
    },
  ],

  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    published: "November 3, 2026",
    lastReviewed: "November 3, 2026",
    lastUpdated: "November 3, 2026",
  },
  schemaJson: {
    "@context": "https://schema.org",
    "@graph": [
      webPageNode({
        id: `${SITE_URL}/learn/what-options-are-left-when-nothing-works-for-depression#webpage`,
        name: "What Options Are Left When Nothing Works?",
        url: `${SITE_URL}/learn/what-options-are-left-when-nothing-works-for-depression`,
        description: "Options when nothing has worked for depression.",
      }),
      orgNode,
      faqJsonld(
        faqs,
        `${SITE_URL}/learn/what-options-are-left-when-nothing-works-for-depression#faq`,
      ),
      breadcrumbJsonld(baseCrumbs("When Nothing Works for Depression")),
    ],
  },
  relatedLinks: [
    {
      title: "When Depression Treatment Isn't Working",
      href: "/depression-treatment-not-working",
      description: "Full hub with all options",
    },
    {
      title: "SAINT® TMS",
      href: "/saint",
      description: "5-day accelerated TMS",
    },
    {
      title: "What Is Treatment-Resistant Depression?",
      href: "/learn/what-is-treatment-resistant-depression",
      description: "Definition and options",
    },
    {
      title: "Am I a Candidate?",
      href: "/am-i-a-candidate",
      description: "Request a consultation",
    },
  ],
};
